/**
 * Theme and Style Utilities
 * Centralized styling logic for the entire application
 */

export const DEFAULT_SETTINGS = {
  appearance: {
    theme: 'light',
    primaryColor: '#26256b',
    layoutStyle: 'modern',
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: 0,
    borderRadius: 8
  },
  layout: {
    sidebarWidth: 224,
    headerHeight: 64,
    contentPadding: 24,
    cardBorderRadius: 8,
    shadowIntensity: 'medium'
  }
};

/**
 * Injects global styles based on platform settings
 * Used in both App.js and PlatformSettings.js
 */
export const injectGlobalStyles = (settings) => {
  // Remove existing custom style if present
  const existingStyle = document.getElementById('platform-custom-styles');
  if (existingStyle) {
    existingStyle.remove();
  }

  const appearance = settings?.appearance ?? DEFAULT_SETTINGS.appearance;
  const layout = settings?.layout ?? DEFAULT_SETTINGS.layout;
  const isDark = appearance.theme === 'dark';
  const isSemiDark = appearance.theme === 'semi-dark';

  // Manage theme classes on document root
  document.documentElement.classList.remove('dark', 'semi-dark');
  if (isDark) {
    document.documentElement.classList.add('dark');
  } else if (isSemiDark) {
    document.documentElement.classList.add('semi-dark');
  }

  // Create style element
  const style = document.createElement('style');
  style.id = 'platform-custom-styles';
  style.textContent = `
    :root {
      --app-primary-color: ${appearance.primaryColor};
      --app-layout-style: ${appearance.layoutStyle || 'modern'};
      --app-font-family: ${appearance.fontFamily};
      --app-font-size: ${appearance.fontSize}px;
      --app-font-weight: ${appearance.fontWeight};
      --app-line-height: ${appearance.lineHeight};
      --app-letter-spacing: ${appearance.letterSpacing}em;
      --app-border-radius: ${appearance.borderRadius}px;
      --app-sidebar-width: ${layout.sidebarWidth}px;
      --app-content-padding: ${layout.contentPadding}px;
    }

    ${isDark ? `
      /* ═══════════════════════════════════════════════════
         DARK THEME — Comprehensive refined dark mode
         Covers all Tailwind utility classes used across
         40+ page components with zero dark-mode awareness
         ═══════════════════════════════════════════════════ */

      /* ── Smooth transition for all dark-mode properties ── */
      *, *::before, *::after {
        transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
      }

      /* ── Base ── */
      body {
        background-color: #0B1120 !important;
        color: #E2E8F0 !important;
      }

      /* ── Background layers (depth hierarchy) ── */
      .bg-white  { background-color: #111827 !important; }
      .bg-gray-50  { background-color: #0f1729 !important; }
      .bg-gray-100 { background-color: #1F2937 !important; }
      .bg-gray-200 { background-color: #283548 !important; }
      .bg-gray-300 { background-color: #334155 !important; }
      .bg-gray-400 { background-color: #475569 !important; }

      /* ── Login page exclusion — keep it fully light ── */
      .min-h-screen.bg-gradient-to-br,
      .min-h-screen.bg-gradient-to-br * {
        background-color: revert !important;
        color: revert !important;
        border-color: revert !important;
        box-shadow: revert !important;
      }
      .min-h-screen.bg-gradient-to-br button[type="submit"] {
        color: #fff !important;
      }

      /* ── Text hierarchy (subtle gradations, NOT all white) ── */
      .text-black   { color: #F8FAFC !important; }
      .text-gray-950 { color: #F8FAFC !important; }
      .text-gray-900 { color: #F1F5F9 !important; }
      .text-gray-800 { color: #E2E8F0 !important; }
      .text-gray-700 { color: #CBD5E1 !important; }
      .text-gray-600 { color: #94A3B8 !important; }
      .text-gray-500 { color: #64748B !important; }
      .text-gray-400 { color: #64748B !important; }

      /* ── Headings ── */
      h1, h2, h3, h4, h5, h6 {
        color: #F1F5F9 !important;
      }

      /* ── Inherited text — don't force, let hierarchy work ── */
      p, label, li, td, th, span, div {
        color: inherit;
      }

      /* ── Borders ── */
      .border-gray-100 { border-color: #1E293B !important; }
      .border-gray-200 { border-color: #334155 !important; }
      .border-gray-300 { border-color: #475569 !important; }
      .border-gray-400 { border-color: #64748B !important; }
      .divide-gray-100 > :not([hidden]) ~ :not([hidden]) { border-color: #1E293B !important; }
      .divide-gray-200 > :not([hidden]) ~ :not([hidden]) { border-color: #334155 !important; }
      .divide-gray-300 > :not([hidden]) ~ :not([hidden]) { border-color: #475569 !important; }

      /* ── Ring / focus-visible colors ── */
      .ring-gray-100 { --tw-ring-color: #1E293B !important; }
      .ring-gray-200 { --tw-ring-color: #334155 !important; }
      .ring-gray-300 { --tw-ring-color: #475569 !important; }
      .focus\\:ring-gray-300:focus { --tw-ring-color: #475569 !important; }
      input:focus, select:focus, textarea:focus {
        --tw-ring-color: var(--app-primary-color) !important;
        border-color: var(--app-primary-color) !important;
        outline: none !important;
      }

      /* ── Form elements ── */
      input, select, textarea,
      input[type="text"], input[type="email"], input[type="password"],
      input[type="number"], input[type="tel"], input[type="url"],
      input[type="search"], input[type="date"], input[type="datetime-local"] {
        background-color: #1E293B !important;
        color: #E2E8F0 !important;
        border-color: #334155 !important;
      }
      input::placeholder, textarea::placeholder {
        color: #64748B !important;
      }
      select option {
        background-color: #1E293B !important;
        color: #E2E8F0 !important;
      }
      /* Checkboxes and radios */
      input[type="checkbox"], input[type="radio"] {
        background-color: #1E293B !important;
        border-color: #475569 !important;
      }
      input[type="checkbox"]:checked, input[type="radio"]:checked {
        background-color: var(--app-primary-color) !important;
        border-color: var(--app-primary-color) !important;
      }

      /* ── Tables ── */
      table { border-color: #1E293B !important; }
      table thead, table thead th {
        background-color: #0f1729 !important;
        color: #94A3B8 !important;
        border-color: #1E293B !important;
      }
      table tbody td {
        color: #CBD5E1 !important;
        border-color: #1E293B !important;
      }
      table tbody tr { background-color: transparent; }
      table tbody tr:nth-child(even) {
        background-color: rgba(30, 41, 59, 0.3) !important;
      }
      table tbody tr:hover {
        background-color: #1E293B !important;
      }

      /* ── Cards / panels — add subtle border glow ── */
      [class*="shadow-sm"], [class*="shadow-md"], [class*="shadow-lg"],
      [class*="shadow-xl"] {
        border: 1px solid rgba(51, 65, 85, 0.6);
      }

      /* ── Shadows (layered depth) ── */
      .shadow-sm {
        box-shadow: 0 1px 2px rgba(0,0,0,0.35) !important;
      }
      .shadow, .shadow-md {
        box-shadow: 0 2px 6px rgba(0,0,0,0.4), 0 1px 2px rgba(0,0,0,0.3) !important;
      }
      .shadow-lg {
        box-shadow: 0 6px 16px rgba(0,0,0,0.45), 0 2px 4px rgba(0,0,0,0.3) !important;
      }
      .shadow-xl, .shadow-2xl {
        box-shadow: 0 12px 32px rgba(0,0,0,0.5), 0 4px 8px rgba(0,0,0,0.35) !important;
      }

      /* ── Modals / dialogs / dropdowns ── */
      [role="dialog"], [role="alertdialog"],
      .modal, .dialog,
      [class*="fixed"][class*="inset"][class*="z-"] > div[class*="bg-white"],
      [class*="absolute"][class*="z-"][class*="bg-white"],
      [class*="fixed"][class*="z-50"] {
        background-color: #111827 !important;
        border-color: #334155 !important;
      }
      /* Modal/dialog overlay */
      [class*="fixed"][class*="inset"][class*="bg-black"],
      [class*="fixed"][class*="inset"][class*="bg-gray"] {
        background-color: rgba(0, 0, 0, 0.7) !important;
      }

      /* ── Dropdown / menu items ── */
      [role="menu"], [role="listbox"],
      [class*="dropdown"], [class*="popover"] {
        background-color: #1E293B !important;
        border-color: #334155 !important;
      }
      [role="menuitem"]:hover, [role="option"]:hover {
        background-color: #334155 !important;
      }

      /* ── Tabs ── */
      [role="tablist"] {
        border-color: #334155 !important;
      }
      [role="tab"] {
        color: #94A3B8 !important;
      }
      [role="tab"][aria-selected="true"],
      [role="tab"].active {
        color: var(--app-primary-color) !important;
        border-color: var(--app-primary-color) !important;
      }
      [role="tabpanel"] {
        background-color: #111827 !important;
      }

      /* ── Badges / pills ── */
      .bg-green-50, .bg-green-100  { background-color: rgba(34, 197, 94, 0.12) !important; }
      .bg-red-50, .bg-red-100      { background-color: rgba(239, 68, 68, 0.12) !important; }
      .bg-yellow-50, .bg-yellow-100 { background-color: rgba(234, 179, 8, 0.12) !important; }
      .bg-blue-50, .bg-blue-100    { background-color: rgba(59, 130, 246, 0.12) !important; }
      .bg-purple-50, .bg-purple-100 { background-color: rgba(168, 85, 247, 0.12) !important; }
      .bg-orange-50, .bg-orange-100 { background-color: rgba(249, 115, 22, 0.12) !important; }
      .bg-indigo-50, .bg-indigo-100 { background-color: rgba(99, 102, 241, 0.12) !important; }
      .bg-pink-50, .bg-pink-100    { background-color: rgba(236, 72, 153, 0.12) !important; }
      .bg-cyan-50, .bg-cyan-100    { background-color: rgba(6, 182, 212, 0.12) !important; }
      .bg-teal-50, .bg-teal-100    { background-color: rgba(20, 184, 166, 0.12) !important; }
      .bg-emerald-50, .bg-emerald-100 { background-color: rgba(16, 185, 129, 0.12) !important; }

      /* Badge borders */
      .border-green-200  { border-color: rgba(34, 197, 94, 0.25) !important; }
      .border-red-200    { border-color: rgba(239, 68, 68, 0.25) !important; }
      .border-yellow-200 { border-color: rgba(234, 179, 8, 0.25) !important; }
      .border-blue-200   { border-color: rgba(59, 130, 246, 0.25) !important; }
      .border-purple-200 { border-color: rgba(168, 85, 247, 0.25) !important; }
      .border-orange-200 { border-color: rgba(249, 115, 22, 0.25) !important; }
      .border-indigo-200 { border-color: rgba(99, 102, 241, 0.25) !important; }

      /* ── Semantic text colors (keep intact) ── */
      .text-green-600, .text-green-700, .text-green-800 { color: #4ade80 !important; }
      .text-red-600, .text-red-700, .text-red-800       { color: #f87171 !important; }
      .text-yellow-600, .text-yellow-700, .text-yellow-800 { color: #fbbf24 !important; }
      .text-orange-600, .text-orange-700, .text-orange-800 { color: #fb923c !important; }
      .text-purple-600, .text-purple-700, .text-purple-800 { color: #c084fc !important; }
      .text-indigo-600, .text-indigo-700 { color: #a5b4fc !important; }
      .text-cyan-600, .text-cyan-700 { color: #22d3ee !important; }
      .text-teal-600, .text-teal-700 { color: #2dd4bf !important; }
      .text-emerald-600, .text-emerald-700 { color: #34d399 !important; }

      /* ── Buttons (secondary/ghost) ── */
      button.bg-white, a.bg-white,
      button.bg-gray-50, button.bg-gray-100,
      a.bg-gray-50, a.bg-gray-100 {
        background-color: #1E293B !important;
        color: #E2E8F0 !important;
        border-color: #334155 !important;
      }
      button.bg-white:hover, a.bg-white:hover,
      button.bg-gray-50:hover, button.bg-gray-100:hover,
      a.bg-gray-50:hover, a.bg-gray-100:hover {
        background-color: #334155 !important;
      }

      /* ── Hover state utilities ── */
      .hover\\:bg-gray-50:hover  { background-color: #1E293B !important; }
      .hover\\:bg-gray-100:hover { background-color: #283548 !important; }
      .hover\\:bg-gray-200:hover { background-color: #334155 !important; }

      /* ── Progress bars ── */
      [role="progressbar"],
      .bg-gray-200[class*="rounded-full"],
      .bg-gray-100[class*="rounded-full"] {
        background-color: #1E293B !important;
      }

      /* ── Tooltips ── */
      [role="tooltip"] {
        background-color: #1E293B !important;
        color: #E2E8F0 !important;
        border: 1px solid #334155 !important;
      }

      /* ── Alerts / notification cards ── */
      [role="alert"] {
        background-color: #1E293B !important;
        border-color: #334155 !important;
        color: #E2E8F0 !important;
      }

      /* ── SVG Icons — Only nav/header icons get forced light.
         Content area icons inherit from parent (preserves colored icons). ── */
      header svg, [role="banner"] svg, [role="navigation"] svg {
        color: #CBD5E1 !important;
      }
      /* SVGs inside nav/aside use a milder rule that doesn't break colored indicators */
      nav svg, aside svg {
        color: #CBD5E1;
      }
      /* Content area SVGs — generic dark icons */
      main svg {
        color: #94A3B8;
      }
      /* Preserve explicitly colored SVGs */
      svg.text-green-500, svg.text-green-600, svg.text-red-500, svg.text-red-600,
      svg.text-yellow-500, svg.text-yellow-600, svg.text-blue-500, svg.text-blue-600,
      svg.text-purple-500, svg.text-purple-600, svg.text-orange-500, svg.text-orange-600,
      svg[class*="text-green"], svg[class*="text-red"],
      svg[class*="text-yellow"], svg[class*="text-orange"],
      svg[class*="text-purple"], svg[class*="text-indigo"],
      svg[class*="text-cyan"], svg[class*="text-teal"],
      svg[class*="text-emerald"], svg[class*="text-pink"] {
        color: inherit !important;
      }

      /* ── Scrollbar ── */
      ::-webkit-scrollbar { width: 8px; height: 8px; }
      ::-webkit-scrollbar-track { background: #0B1120; }
      ::-webkit-scrollbar-thumb { background: #334155; border-radius: 4px; }
      ::-webkit-scrollbar-thumb:hover { background: #475569; }

      /* ── Horizontal rule / dividers ── */
      hr { border-color: #1E293B !important; }

      /* ── Code / pre blocks ── */
      code, pre {
        background-color: #0f1729 !important;
        color: #E2E8F0 !important;
        border-color: #1E293B !important;
      }

      /* ── Breadcrumbs / navigation helpers ── */
      nav[aria-label="breadcrumb"], nav[aria-label="Breadcrumb"] {
        background-color: transparent !important;
      }
      nav[aria-label="breadcrumb"] a, nav[aria-label="Breadcrumb"] a {
        color: #94A3B8 !important;
      }

      /* ── Empty states, placeholder illustrations ── */
      .text-gray-300 svg, .text-gray-400 svg {
        color: #475569 !important;
      }

      /* ── Keep primary-color text as-is ── */
      [class*="text-blue"]:not(svg) {
        color: var(--app-primary-color) !important;
      }

      /* ── Active sidebar menu item ── */
      nav a[class*="bg-blue"], aside a[class*="bg-blue"],
      nav button[class*="bg-blue"], aside button[class*="bg-blue"] {
        color: #ffffff !important;
        background-color: var(--app-primary-color) !important;
      }

      /* ── Hover states for nav ── */
      nav a:hover:not([data-active="true"]),
      aside a:hover:not([data-active="true"]) {
        background-color: rgba(255,255,255,0.06) !important;
      }

      /* ── Stripe-like data rows ── */
      .even\\:bg-gray-50:nth-child(even) {
        background-color: rgba(30, 41, 59, 0.3) !important;
      }
      .odd\\:bg-white:nth-child(odd) {
        background-color: #111827 !important;
      }

      /* ── Status dots / indicators — preserve color ── */
      [class*="bg-green-500"], [class*="bg-red-500"],
      [class*="bg-yellow-500"], [class*="bg-orange-500"],
      [class*="bg-green-400"], [class*="bg-red-400"],
      [class*="bg-yellow-400"], [class*="bg-orange-400"] {
        background-color: revert !important;
      }
    ` : ''}

    ${isSemiDark ? `
      /* ═══════════════════════════════════════════════════
         SEMI-DARK THEME — Dark sidebar/navbar, light content
         Only targets navigation chrome; content stays light.
         ═══════════════════════════════════════════════════ */

      /* ── Scrollbar — slightly muted ── */
      ::-webkit-scrollbar { width: 8px; height: 8px; }
      ::-webkit-scrollbar-track { background: #F1F5F9; }
      ::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 4px; }
      ::-webkit-scrollbar-thumb:hover { background: #94A3B8; }
    ` : ''}

    body {
      font-family: var(--app-font-family) !important;
      font-size: var(--app-font-size) !important;
      line-height: var(--app-line-height) !important;
      letter-spacing: var(--app-letter-spacing) !important;
      font-weight: var(--app-font-weight) !important;
    }

    * {
      font-family: var(--app-font-family) !important;
    }
    
    /* Apply Typography to All Text Elements */
    // p:not([data-exclude]), span:not([data-exclude]), div:not([data-logo] div), label, a, li, td, th, button, input, select, textarea {
    //   font-size: var(--app-font-size) !important;
    //   line-height: var(--app-line-height) !important;
    //   letter-spacing: var(--app-letter-spacing) !important;
    // }
    
    // /* Apply font weight to body text elements */
    // p:not([data-exclude]), span:not([data-exclude]), div:not([class*="font-"]):not([data-logo] div), label, a, li, td {
    //   font-weight: var(--app-font-weight) !important;
    // }

    /* Exclude logo area from global font overrides */
    [data-logo] > span {
      font-size: 20px !important;
      font-weight: 600 !important;
      line-height: normal !important;
      letter-spacing: normal !important;
    }
    [data-logo] > div span {
      font-size: 22px !important;
      font-weight: 700 !important;
    }

    /* Primary Color - Background */
    .bg-blue-600, .bg-blue-500, .bg-blue-700, 
    [class*="bg-blue"] {
      background-color: var(--app-primary-color) !important;
    }
    
    /* Selected/Active Menu Items - Force white text on primary background */
    nav a[class*="bg-blue"], nav button[class*="bg-blue"],
    aside a[class*="bg-blue"], aside button[class*="bg-blue"],
    [class*="bg-blue"] a, [class*="bg-blue"] button,
    [class*="bg-blue"] span, [class*="bg-blue"] div {
      color: #ffffff !important;
    }
    
    /* Icons on primary color backgrounds - Force white */
    [class*="bg-blue"] svg,
    button[class*="bg-blue"] svg,
    a[class*="bg-blue"] svg,
    .bg-blue-600 svg, .bg-blue-500 svg, .bg-blue-700 svg {
      color: #ffffff !important;
    }

    /* Primary Color - Text */
    .text-blue-600, .text-blue-500, .text-blue-700,
    [class*="text-blue"] {
      color: var(--app-primary-color) !important;
    }

    /* Primary Color - Border */
    .border-blue-600, .border-blue-500, .border-blue-700,
    [class*="border-blue"] {
      border-color: var(--app-primary-color) !important;
    }

    /* Primary Color - Hover States */
    .hover\\:bg-blue-700:hover,
    .hover\\:bg-blue-600:hover,
    .hover\\:bg-blue-500:hover,
    [class*="hover:bg-blue"]:hover {
      background-color: var(--app-primary-color) !important;
      filter: brightness(0.9);
    }

    .hover\\:text-blue-600:hover,
    .hover\\:text-blue-700:hover,
    [class*="hover:text-blue"]:hover {
      color: var(--app-primary-color) !important;
    }

    /* Primary Color - Focus States */
    .focus\\:ring-blue-500:focus,
    .focus\\:border-blue-500:focus,
    [class*="focus:ring-blue"]:focus,
    [class*="focus:border-blue"]:focus {
      --tw-ring-color: var(--app-primary-color) !important;
      border-color: var(--app-primary-color) !important;
    }

    /* Border Radius - Apply to all rounded elements */
    button, 
    input, select, textarea,
    .rounded, .rounded-sm, .rounded-md, .rounded-lg, .rounded-xl, .rounded-2xl, .rounded-3xl,
    [class*="rounded"],
    .card, .modal, .dialog, .dropdown, .tooltip,
    img, video, iframe {
      border-radius: var(--app-border-radius) !important;
    }
    
    /* Force perfect circles for loaders/spinners - HIGHEST PRIORITY */
    .rounded-full,
    [class*="rounded-full"],
    .animate-spin,
    [class*="animate-spin"],
    div.animate-spin,
    div.rounded-full,
    div[class*="animate-spin"],
    div[class*="rounded-full"],
    .spinner, .loader,
    [class*="spinner"],
    [class*="loader"] {
      border-radius: 9999px !important;
    }
    
    /* Ensure spinner/loader containers maintain circular shape */
    div[class~="animate-spin"][class~="rounded-full"] {
      border-radius: 9999px !important;
    }
    
    /* Border Radius for specific UI components - EXCLUDE header and aside */
    .bg-white, .bg-gray-50, .bg-gray-100,
    [class*="shadow"], [class*="border"] {
      border-radius: var(--app-border-radius) !important;
    }
    
    /* CRITICAL: Override border-radius for header and aside - must come AFTER broad selectors */
    header,
    header.bg-white,
    header.bg-gray-50, 
    header.bg-gray-100,
    header[class*="shadow"],
    header[class*="border"],
    header *,
    header .bg-white,
    header .bg-gray-50,
    header .bg-gray-100,
    header [class*="shadow"],
    header [class*="border"],
    aside,
    aside.bg-white,
    aside.bg-gray-50,
    aside.bg-gray-100,
    aside[class*="shadow"],
    aside[class*="border"],
    aside *,
    aside .bg-white,
    aside .bg-gray-50,
    aside .bg-gray-100,
    aside [class*="shadow"],
    aside [class*="border"] {
      border-radius: 0 !important;
    }
    
    /* Remove border radius from header, aside and all their children */
    header, aside,
    header *, aside *,
    header [class*="rounded"],
    aside [class*="rounded"],
    header [class*="shadow"],
    aside [class*="shadow"],
    header [class*="border"],
    aside [class*="border"],
    header button,
    aside button,
    header input,
    aside input,
    header select,
    aside select,
    header textarea,
    aside textarea {
      border-radius: 0 !important;
    }
    
    /* Layout variables are set via CSS custom properties but not forced with !important
       to allow component-level Tailwind classes to work properly */

    /* Active/Selected Sidebar Menu Item - Use Primary Color */
    a[data-active="true"],
    nav a[data-active="true"],
    aside a[data-active="true"] {
      background: var(--app-primary-color) !important;
      color: #ffffff !important;
    }
    a[data-active="true"] svg,
    nav a[data-active="true"] svg,
    aside a[data-active="true"] svg {
      color: #ffffff !important;
    }

    /* Fix: Sign In button text should always be white */
    .min-h-screen.bg-gradient-to-br .bg-white button[type="submit"] {
      color: #fff !important;
    }

    /* =====================================================
       SIDEBAR — Restore component-specific typography
       Must come LAST so it wins over global !important rules
       above. Uses double-class specificity (0,2,0) to beat
       single-class+element selectors like span:not([data-exclude]).
       ===================================================== */
    .sidebar .sidebar-logo-name {
      font-size: 1rem !important;
      font-weight: 700 !important;
      letter-spacing: -0.045em !important;
      line-height: 1.15 !important;
    }
    .sidebar .sidebar-logo-tagline {
      font-size: 0.7rem !important;
      font-weight: 700 !important;
      letter-spacing: 0.15em !important;
      line-height: 1.3 !important;
    }
    .sidebar .sidebar-nav-item {
      font-size: 0.8125rem !important;
      font-weight: 500 !important;
      letter-spacing: normal !important;
      line-height: normal !important;
    }
    .sidebar .sidebar-nav-item.sidebar-nav-item-active {
      font-weight: 600 !important;
    }
    .sidebar .sidebar-group-label {
      font-size: 0.6rem !important;
      letter-spacing: 0.09em !important;
      border-radius: 0.375rem !important;
      line-height: normal !important;
    }
    .sidebar .sidebar-logout-btn {
      font-size: 0.8125rem !important;
      font-weight: 600 !important;
      letter-spacing: normal !important;
      border-radius: 0.5rem !important;
    }
    .sidebar .sidebar-close-btn {
      border-radius: 0.5rem !important;
    }
  `;

  document.head.appendChild(style);
};

/**
 * Get saved platform settings from localStorage
 */
export const getPlatformSettings = () => {
  try {
    const saved = localStorage.getItem('platformSettings');
    return saved ? JSON.parse(saved) : DEFAULT_SETTINGS;
  } catch (error) {
    console.error('Error reading platform settings:', error);
    return DEFAULT_SETTINGS;
  }
};

/**
 * Save platform settings to localStorage
 */
export const savePlatformSettings = (settings) => {
  try {
    localStorage.setItem('platformSettings', JSON.stringify(settings));
    injectGlobalStyles(settings);
    return true;
  } catch (error) {
    console.error('Error saving platform settings:', error);
    return false;
  }
};
